// Pestaña "Capacidades" de la pantalla del asistente: los pedidos que el asistente registró cuando le
// pidieron algo que todavía no sabe hacer, su estado en el constructor automático, el informe de lo
// que se hizo, y los botones para cancelar un pedido o deshacer una capacidad ya incorporada.
// Arriba, el interruptor del constructor automático y su tope diario (solo administrador).
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Filter, Loader2, RefreshCw, Undo2, Ban, Wrench, CheckCircle2 } from 'lucide-react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { useCan } from '../../lib/rbac';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { Pagination } from '../../components/shared/Pagination';
import { usePagination } from '../../hooks/usePagination';
import { AssistantMarkdown } from '../../components/AssistantMarkdown';
import {
  asPage,
  fmtDateTime,
  loadMeta,
  useSeesAllSellers,
  CAPABILITY_STATUS_LABEL,
  CAPABILITY_KIND_LABEL,
} from './shared';

const capStatusLabel = (s: string): string => CAPABILITY_STATUS_LABEL[s] ?? s;
const capKindLabel = (k: string): string => CAPABILITY_KIND_LABEL[k] ?? k;

type CapStatus =
  | 'requested'
  | 'building'
  | 'gate'
  | 'review'
  | 'fixing'
  | 'deploying'
  | 'done'
  | 'failed'
  | 'needs_owner'
  | 'rejected'
  | 'cancelled';

interface CapRow {
  id: number;
  seller_id: number | null;
  requested_by: string;
  kind: string;
  title: string;
  target_request_id: number | null;
  status: CapStatus;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  deployed_at: string | null;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  approved_by: string | null;
  /** "Pedido" que todavía no aprobó el dueño: el constructor no lo toma. */
  needs_approval: boolean;
}

interface CapDetail extends CapRow {
  description: string;
  what_for: string | null;
  example: string | null;
  branch: string | null;
  commits: unknown;
  builder_report: string | null;
  reviewer_verdict: unknown;
  gate_output: string | null;
  revert_request_id: number | null;
  target_title: string | null;
}

interface BuilderState {
  enabled: boolean;
  max_per_day: number;
  started_today: number;
  awaiting_approval: number;
}

function statusTone(s: string): 'ok' | 'warning' | 'error' | 'info' | 'unknown' {
  if (s === 'done') return 'ok';
  if (s === 'failed' || s === 'needs_owner') return 'error';
  if (s === 'requested') return 'info';
  if (s === 'rejected' || s === 'cancelled') return 'unknown';
  return 'info';
}

const IN_PROGRESS = 'requested,building,gate,review,fixing,deploying';
const MAX_PER_PAGE = 200;

const inputCls =
  'px-2 py-1 text-xs bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded text-[var(--color-text)]';
const btnGhost =
  'inline-flex items-center gap-1 px-3 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)] disabled:opacity-40';
type Action = 'cancel' | 'revert' | 'approve';

const btnPrimary =
  'inline-flex items-center gap-1 px-3 py-1 text-xs rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-40';
const btnDanger =
  'inline-flex items-center gap-1 px-3 py-1 text-xs rounded bg-[var(--color-error)] text-white hover:opacity-90 disabled:opacity-40';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-xs py-0.5">
      <span className="w-36 shrink-0 text-[var(--color-text-muted)]">{label}</span>
      <span className="min-w-0 break-words whitespace-pre-wrap text-[var(--color-text)]">{children}</span>
    </div>
  );
}

/** Lista de commits del pedido (el constructor guarda un arreglo de SHA o de objetos). */
function commitsOf(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((c) => {
      if (typeof c === 'string') return c;
      if (c && typeof c === 'object') {
        const o = c as Record<string, unknown>;
        const sha = typeof o.sha === 'string' ? o.sha.slice(0, 10) : '';
        const msg = typeof o.message === 'string' ? o.message : typeof o.subject === 'string' ? o.subject : '';
        return [sha, msg].filter(Boolean).join(' — ');
      }
      return '';
    })
    .filter(Boolean);
}

/** Veredicto del revisor en palabras (`{ ok, blockers, findings }`). */
function verdictText(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const list = (k: string) =>
      Array.isArray(o[k]) ? (o[k] as unknown[]).map((x) => `• ${typeof x === 'string' ? x : JSON.stringify(x)}`) : [];
    const parts: string[] = [];
    if (typeof o.ok === 'boolean') parts.push(o.ok ? 'Aprobado por el revisor.' : 'El revisor NO lo aprobó.');
    const blockers = list('blockers');
    if (blockers.length) parts.push(`Problemas que impiden incorporarlo:\n${blockers.join('\n')}`);
    const findings = list('findings');
    if (findings.length) parts.push(`Observaciones:\n${findings.join('\n')}`);
    return parts.length ? parts.join('\n\n') : JSON.stringify(v, null, 2);
  }
  return String(v);
}

// ── Detalle expandible de un pedido ──────────────────────────────────────────

function CapabilityDetail({
  id,
  reloadKey,
  onChanged,
}: {
  id: number;
  reloadKey: number;
  onChanged: () => void;
}) {
  const can = useCan();
  const me = useAuthStore((s) => s.user);
  const seesAll = useSeesAllSellers();
  const [data, setData] = useState<CapDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Action | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [showGate, setShowGate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<CapDetail>(`/assistant/capabilities/${id}`);
      if (res.success && res.data) setData(res.data);
      else setError(res.error || 'No se pudo cargar el pedido.');
    } catch {
      setError('No se pudo cargar el pedido.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  const act = async (what: Action) => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.post<CapDetail>(`/assistant/capabilities/${id}/${what}`, {});
      if (res.success) {
        setMsg({
          ok: true,
          text:
            what === 'cancel'
              ? 'Pedido cancelado.'
              : what === 'approve'
                ? 'Pedido aprobado: el constructor lo va a tomar cuando le toque.'
                : `Listo: se anotó el pedido #${res.data?.id ?? ''} para deshacer esta capacidad. El constructor lo va a procesar.`,
        });
        setConfirm(null);
        onChanged();
        void load();
      } else {
        setMsg({ ok: false, text: res.error || 'No se pudo completar.' });
      }
    } catch {
      setMsg({ ok: false, text: 'No se pudo completar.' });
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="p-3 text-xs text-[var(--color-text-muted)] flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Cargando…
      </div>
    );
  }
  if (error || !data) {
    return <div className="p-3 text-xs text-[var(--color-error)]">{error || 'No se pudo cargar el pedido.'}</div>;
  }

  const commits = commitsOf(data.commits);
  const verdict = verdictText(data.reviewer_verdict);
  const isOwnerProblem = data.status === 'needs_owner' || data.status === 'failed';
  const isAuthorOrAdmin = !!me?.is_admin || me?.username === data.requested_by;
  const canCancel = data.status === 'requested' && can('assistant.actions') && isAuthorOrAdmin;
  const canApprove = !!data.needs_approval && can('assistant.manage') && seesAll;
  const canRevert =
    data.status === 'done' &&
    data.kind !== 'revert' &&
    data.revert_request_id == null &&
    can('assistant.actions') &&
    isAuthorOrAdmin;

  return (
    <div className="p-3 space-y-3 bg-[var(--color-bg-secondary)]">
      <div>
        <Field label="Tipo">{capKindLabel(data.kind)}</Field>
        {data.kind === 'revert' && data.target_request_id != null && (
          <Field label="Deshace el pedido">
            #{data.target_request_id}
            {data.target_title ? ` — ${data.target_title}` : ''}
          </Field>
        )}
        <Field label="Qué se pidió">{data.description}</Field>
        {data.what_for && <Field label="Para qué">{data.what_for}</Field>}
        {data.example && <Field label="Ejemplo">{data.example}</Field>}
        {data.approved_at && (
          <Field label="Aprobado">
            {fmtDateTime(data.approved_at)}
            {data.approved_by ? ` por ${data.approved_by}` : ''}
          </Field>
        )}
        <Field label="Empezó a construirse">{fmtDateTime(data.started_at)}</Field>
        <Field label="Terminó">{fmtDateTime(data.finished_at)}</Field>
        {data.revert_request_id != null && (
          <Field label="Reversión">Pedido #{data.revert_request_id} para deshacer esta capacidad</Field>
        )}
      </div>

      {data.error && (
        <div
          className={`rounded p-2 text-xs whitespace-pre-wrap ${
            isOwnerProblem
              ? 'bg-[var(--color-error-light)] text-[var(--color-error)] font-medium'
              : 'bg-[var(--color-bg-card)] text-[var(--color-text-secondary)]'
          }`}
        >
          {data.status === 'needs_owner' ? 'Necesita al dueño: ' : 'Nota: '}
          {data.error}
        </div>
      )}

      {data.builder_report && (
        <div className="space-y-1">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
            Informe del constructor
          </div>
          <div className="rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] p-2 text-sm max-h-96 overflow-auto">
            <AssistantMarkdown text={data.builder_report} />
          </div>
        </div>
      )}

      {verdict && (
        <div className="space-y-1">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
            Revisión
          </div>
          <div className="rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] p-2 text-xs whitespace-pre-wrap max-h-60 overflow-auto">
            {verdict}
          </div>
        </div>
      )}

      {commits.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
            Cambios incorporados
          </div>
          <ul className="text-xs font-mono space-y-0.5">
            {commits.map((c) => (
              <li key={c} className="break-all">
                {c}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.gate_output && (
        <div className="space-y-1">
          <button type="button" className={btnGhost} onClick={() => setShowGate((v) => !v)}>
            {showGate ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Resultado de las pruebas automáticas
          </button>
          {showGate && (
            <pre className="rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] p-2 text-[11px] whitespace-pre-wrap break-all max-h-80 overflow-auto">
              {data.gate_output}
            </pre>
          )}
        </div>
      )}

      {(canCancel || canRevert || canApprove) && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {confirm === null && canApprove && (
            <button type="button" className={btnGhost} onClick={() => setConfirm('approve')} disabled={busy}>
              <CheckCircle2 size={12} /> Aprobar
            </button>
          )}
          {confirm === null && canCancel && (
            <button type="button" className={btnGhost} onClick={() => setConfirm('cancel')} disabled={busy}>
              <Ban size={12} /> Cancelar pedido
            </button>
          )}
          {confirm === null && canRevert && (
            <button type="button" className={btnGhost} onClick={() => setConfirm('revert')} disabled={busy}>
              <Undo2 size={12} /> Revertir
            </button>
          )}
          {confirm !== null && (
            <>
              <span className="text-xs text-[var(--color-text)]">
                {confirm === 'cancel'
                  ? '¿Cancelar este pedido? El constructor no lo va a tomar.'
                  : confirm === 'approve'
                    ? '¿Aprobar este pedido? El constructor lo va a programar, probar e incorporar al sistema.'
                    : '¿Deshacer esta capacidad? Se anota un pedido que saca los cambios y vuelve a desplegar el sistema.'}
              </span>
              <button
                type="button"
                className={confirm === 'approve' ? btnPrimary : btnDanger}
                onClick={() => void act(confirm)}
                disabled={busy}
              >
                {busy && <Loader2 size={12} className="animate-spin" />}
                {confirm === 'cancel' ? 'Sí, cancelar' : confirm === 'approve' ? 'Sí, aprobar' : 'Sí, revertir'}
              </button>
              <button type="button" className={btnGhost} onClick={() => setConfirm(null)} disabled={busy}>
                No
              </button>
            </>
          )}
        </div>
      )}
      {msg && (
        <div className={`text-xs ${msg.ok ? 'text-[var(--color-success)]' : 'text-[var(--color-error)]'}`}>{msg.text}</div>
      )}
    </div>
  );
}

// ── Interruptor del constructor ──────────────────────────────────────────────

function BuilderSwitch({ reloadKey }: { reloadKey: number }) {
  const can = useCan();
  const seesAll = useSeesAllSellers();
  const canManage = can('assistant.manage') && seesAll;
  const [state, setState] = useState<BuilderState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOn, setConfirmOn] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get<BuilderState>('/assistant/capabilities/builder');
      if (res.success && res.data) {
        setState(res.data);
        setError(null);
      } else setError(res.error || 'No se pudo leer el estado del constructor.');
    } catch {
      setError('No se pudo leer el estado del constructor.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  const save = async (patch: { enabled: boolean; max_per_day?: number }) => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.put<BuilderState>('/assistant/capabilities/builder', patch);
      if (res.success && res.data) {
        setState(res.data);
        setConfirmOn(false);
      } else setError(res.error || 'No se pudo guardar.');
    } catch {
      setError('No se pudo guardar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-3">
      <Wrench className="w-4 h-4 text-[var(--color-text-secondary)]" />
      <span className="text-sm font-medium text-[var(--color-text)]">Constructor automático</span>
      {state ? (
        <>
          <StatusBadge
            status={state.enabled ? 'ok' : 'unknown'}
            label={state.enabled ? 'Prendido' : 'Apagado'}
            size="sm"
          />
          <span className="text-xs text-[var(--color-text-secondary)]">
            Hoy: {state.started_today} de {state.max_per_day} construcciones
          </span>
          {state.awaiting_approval > 0 && (
            <StatusBadge status="warning" label={`Esperando aprobación: ${state.awaiting_approval}`} size="sm" />
          )}
          {canManage && (
            <>
              <label className="text-xs text-[var(--color-text-secondary)] flex items-center gap-1">
                Tope por día
                <select
                  className={inputCls}
                  value={state.max_per_day}
                  disabled={busy}
                  onChange={(e) => void save({ enabled: state.enabled, max_per_day: Number(e.target.value) })}
                  aria-label="Tope de construcciones por día"
                >
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              {state.enabled ? (
                <button type="button" className={btnGhost} disabled={busy} onClick={() => void save({ enabled: false })}>
                  Apagar
                </button>
              ) : confirmOn ? (
                <>
                  <span className="text-xs text-[var(--color-text)]">
                    Al prenderlo, el sistema construye e incorpora solo los pedidos que aprobaste (hasta el tope diario) y se
                    reinicia para aplicarlos. ¿Prender?
                  </span>
                  <button type="button" className={btnDanger} disabled={busy} onClick={() => void save({ enabled: true })}>
                    {busy && <Loader2 size={12} className="animate-spin" />}
                    Sí, prender
                  </button>
                  <button type="button" className={btnGhost} disabled={busy} onClick={() => setConfirmOn(false)}>
                    No
                  </button>
                </>
              ) : (
                <button type="button" className={btnGhost} disabled={busy} onClick={() => setConfirmOn(true)}>
                  Prender
                </button>
              )}
            </>
          )}
        </>
      ) : (
        !error && <Loader2 size={14} className="animate-spin text-[var(--color-text-muted)]" />
      )}
      {error && <span className="text-xs text-[var(--color-error)]">{error}</span>}
    </div>
  );
}

// ── Pestaña ──────────────────────────────────────────────────────────────────

export default function CapabilitiesTab() {
  const sellers = useUIStore((s) => s.sellers);
  const pag = usePagination('assistant-capabilities');
  const [rows, setRows] = useState<CapRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reqSeq = useRef(0);
  // Etiquetas de estado y tipo desde el servidor (/meta); mientras tanto, las de respaldo.
  const [, setLabelsLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    void loadMeta().then(() => {
      if (alive) setLabelsLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const sellerName = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of sellers) map.set(s.id, s.nickname);
    return (id: number | null) => (id == null ? 'General' : map.get(id) ?? String(id));
  }, [sellers]);

  useEffect(() => {
    if (pag.perPage > MAX_PER_PAGE) pag.setPerPage(MAX_PER_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pag.perPage]);

  const loadList = useCallback(async () => {
    if (pag.perPage > MAX_PER_PAGE) return;
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: String(pag.perPage),
        offset: String((pag.page - 1) * pag.perPage),
      });
      if (status) params.set('status', status);
      const res = await api.get<unknown>(`/assistant/capabilities?${params}`);
      if (seq !== reqSeq.current) return;
      if (res.success) {
        const page = asPage<CapRow>(res.data);
        setRows(page.rows);
        pag.setTotal(page.total);
      } else {
        setRows([]);
        pag.setTotal(0);
        setError(res.error || 'No se pudieron cargar los pedidos.');
      }
    } catch {
      if (seq === reqSeq.current) setError('No se pudieron cargar los pedidos.');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pag.page, pag.perPage, status]);

  useEffect(() => {
    void loadList();
  }, [loadList, reloadKey]);

  useEffect(() => {
    pag.resetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const changed = () => setReloadKey((k) => k + 1);

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--color-text-secondary)]">
        Cosas que le pidieron al asistente y que todavía no sabía hacer. El constructor automático las programa, las
        prueba, las revisa y las incorpora al sistema, pero solo después de que las aprobás. Hacé clic en una fila para ver el informe.
      </p>

      <BuilderSwitch reloadKey={reloadKey} />

      <div className="flex flex-wrap items-center gap-2 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-3">
        <Filter className="w-4 h-4 text-[var(--color-text-secondary)]" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls} aria-label="Estado">
          <option value="">Todos los estados</option>
          <option value={IN_PROGRESS}>En curso</option>
          {Object.keys(CAPABILITY_STATUS_LABEL).map((k) => (
            <option key={k} value={k}>
              {capStatusLabel(k)}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={changed}
          title="Actualizar"
          aria-label="Actualizar"
          className="p-1 rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="rounded-lg p-3 text-sm bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
              <th className="px-3 py-2 w-6" />
              <th className="px-3 py-2">Pedido</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2">Empresa</th>
              <th className="px-3 py-2">Quién lo pidió</th>
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2">Desplegado el</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-xs text-[var(--color-text-muted)]">
                  {loading ? 'Cargando…' : 'No hay pedidos con este filtro.'}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr
                  className="border-b border-[var(--color-border)] cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                  onClick={() => setOpen((o) => (o === r.id ? null : r.id))}
                >
                  <td className="px-3 py-2 text-[var(--color-text-muted)]">
                    {open === r.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-xs text-[var(--color-text-muted)] mr-1">#{r.id}</span>
                    <span className="text-[var(--color-text)]">{r.title}</span>
                    {r.kind === 'revert' && (
                      <span className="ml-1 text-xs text-[var(--color-text-muted)]">(deshacer)</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.needs_approval ? (
                      <StatusBadge status="warning" label="Esperando tu aprobación" size="sm" />
                    ) : (
                      <StatusBadge status={statusTone(r.status)} label={capStatusLabel(r.status)} size="sm" />
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{sellerName(r.seller_id)}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{r.requested_by}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap text-[var(--color-text-secondary)]">
                    {fmtDateTime(r.created_at)}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap text-[var(--color-text-secondary)]">
                    {fmtDateTime(r.deployed_at)}
                  </td>
                </tr>
                {open === r.id && (
                  <tr className="border-b border-[var(--color-border)]">
                    <td colSpan={7} className="p-0">
                      <CapabilityDetail id={r.id} reloadKey={reloadKey} onChanged={changed} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={pag.page}
        perPage={pag.perPage}
        total={pag.total}
        totalPages={pag.totalPages}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />
    </div>
  );
}
